import TagManager from 'react-gtm-module'
import { telemetryStorage } from 'app/modules/auth/helper/storage'
import type { GTMDataLayerEvent, GTMConsentEvent, GTMPageViewEvent } from '../types/gtm'
import { isInIframe } from './functions'

/**
 * Check if GTM has been initialized
 * @returns {boolean} True if GTM script is loaded and dataLayer exists
 */
export function isGTMInitialized(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  const hasDataLayer = window.dataLayer !== undefined && Array.isArray(window.dataLayer)
  const hasGTMStartEvent = hasDataLayer && window.dataLayer.some((item: any) => item.event === 'gtm.js')

  return hasGTMStartEvent
}

/**
 * Push event to GTM dataLayer
 * @param event Event data to push to dataLayer
 */
export function trackEvent(event: GTMDataLayerEvent): void {
  const init = isGTMInitialized()

  if (init) {
    TagManager.dataLayer({
      dataLayer: event,
    })
  }
}

/**
 * Push analytics consent status to dataLayer
 * @param granted Whether consent is granted or denied
 */
export function pushAnalyticsConsent(granted: boolean): void {
  const event: GTMConsentEvent = {
    event: granted ? 'analytics_consent_granted' : 'analytics_consent_revoked',
    consent_status: granted ? 'granted' : 'denied',
  }
  trackEvent(event)
}

/**
 * Extract domain from URL
 * @param url Full URL
 */
function extractDomain(url: string): string {
  try {
    const urlObj = new URL(url)
    return urlObj.hostname
  } catch {
    return ''
  }
}

/**
 * Push page view event to dataLayer
 * @param routeTemplate Optional route template (e.g., /scan/:id)
 * @param userId Optional user ID if authenticated
 */
export function pushPageView(routeTemplate?: string, userId?: string): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return
  }

  const fullUrl = window.location.href
  const pathname = window.location.pathname
  const hostname = window.location.hostname
  const title = document.title
  const referrer = document.referrer || undefined
  const referringDomain = referrer ? extractDomain(referrer) : undefined

  const event: GTMPageViewEvent = {
    event: 'page_view',
    page_url: fullUrl,
    page_path: pathname,
    page_title: title,
    page_hostname: hostname,
  }

  if (routeTemplate) {
    event.route_template = routeTemplate
  }

  if (referrer) {
    event.referrer = referrer
  }

  if (referringDomain) {
    event.referring_domain = referringDomain
  }

  if (userId !== undefined && userId !== null) {
    event.user_id = userId
  }

  trackEvent(event)
}

/**
 * Push one-time product pageview event in community
 * This event fires only once per session to indicate product usage
 */
export function pushGtmProductInteraction(): void {
  if (typeof window === 'undefined' || telemetryStorage.isProductPageviewFired()) {
    return
  }

  trackEvent({
    event: 'product_pageview',
  })

  telemetryStorage.markProductPageviewFired()
}

/**
 * Post one-time product interaction event to parent window for iframed version,
 * when the application is running  - typically - on myopswat portal.
 * This event fires only once per session to indicate product usage.
 * @param targetOrigin The origin to post message to (e.g., myopswat portal url)
 */
export function postIframeProductInteraction(targetOrigin: string): void {
  if (!isInIframe() || telemetryStorage.isProductInteractionFired()) {
    return
  }

  window.parent.postMessage(
    {
      event: 'product_interaction',
      product_name: 'filescan',
    },
    targetOrigin
  )

  telemetryStorage.markProductInteractionFired()
}
